/**
 * SignInForm
 *
 * Controlled email/password sign-in form with minimal UI helpers and
 * a password visibility toggle. Delegates submit via props.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';

export default function SignInForm({ onSubmit, isLoading, onInputChange }) {
  const [showPassword, setShowPassword] = useState(false);
  
  const togglePasswordVisibility = () => {
    // Clear any error messages when toggling password visibility
    if (onInputChange) {
      onInputChange();
    }
    setShowPassword(!showPassword);
  };

  // Handle submit with loading state
  const handleSubmit = (e) => {
    if (isLoading) {
      e.preventDefault();
      return;
    }
    onSubmit(e);
  };

  return (
    <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] px-6 py-8 mt-6 sm:px-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            Email address
          </label>
          <div className="mt-2">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              onInput={onInputChange}
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
              Password
            </label>
            <Link 
              to="/forgot-password" 
              className="text-sm font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
              onClick={onInputChange}
            >
              Forgot password?
            </Link>
          </div>
          <div className="mt-2 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              onInput={onInputChange}
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className={`flex w-full justify-center items-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3.5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 ${isLoading ? 'opacity-95 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <ButtonSpinner color="white" className="mr-2" />
                <span>Signing in...</span>
              </div>
            ) : (
              <span>Sign in</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}